import { useCallback, useState } from 'react';

import useIsOverflowing from 'hooks/useIsOverflowing';

/**
 * Drives the reveal of a value its column has clipped.
 *
 * Two things it deliberately owns rather than delegating to `Popover`:
 *
 * 1. **When the popover exists at all.** A KDS `Popover` registers a
 *    capture-phase `document` click listener and its hover listeners in
 *    effects whose dependencies it rebuilds every render, so a mounted one
 *    costs a listener churn per render even while hidden. A table page holds
 *    hundreds of truncating cells, so the popover is mounted only for the one
 *    the pointer is actually on — `isRevealed` — and driven by the controlled
 *    `visible` prop rather than by `manageEvents`.
 * 2. **Focus as well as hover.** `revealProps` carries `onFocus`/`onBlur`
 *    alongside the pointer handlers, so tabbing to a truncated link or button
 *    reveals its value too. Spread them onto whatever the keyboard actually
 *    reaches: the clamped text when it stands alone, or the control that wraps
 *    it. React's focus handlers are delegated from `focusin`/`focusout`, so a
 *    wrapper hears focus landing on a control inside it.
 */
export default function useTruncationReveal<
  TElement extends HTMLElement = HTMLElement,
>() {
  const { ref, isOverflowing, measure } = useIsOverflowing<TElement>();
  const [isPointedAt, setIsPointedAt] = useState(false);

  const reveal = useCallback(() => {
    measure();
    setIsPointedAt(true);
  }, [measure]);

  const conceal = useCallback(() => setIsPointedAt(false), []);

  return {
    /** Goes on the element whose text is clipped — the one that overflows. */
    measureRef: ref,
    isOverflowing,
    /** True only while the pointer or focus is on a value that is clipped. */
    isRevealed: isOverflowing && isPointedAt,
    /**
     * Closes the panel. Hand it to `OverflowPopover`'s `onRequestHide`: a
     * controlled `Popover` routes every hide it decides on — Escape, a scroll
     * that moves the anchor, a click outside — through that callback, and
     * without it those all become no-ops.
     */
    conceal,
    revealProps: {
      onMouseEnter: reveal,
      onMouseLeave: conceal,
      onFocus: reveal,
      onBlur: conceal,
    },
  };
}
